import { CheckCircle, ChevronRight, LocalPhone, LocationOn, Tty } from '@mui/icons-material';
import { Box, Divider, Grid } from '@mui/material';
import { Button, Typography } from '@uhc-tempo/components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { FormContext } from '../../../context/Form.context';
import useDetectMobile from '../../../utils/customHooks/useDetectMobile';
import AverageThresholdTag from './AvgTag.component';
import { useHistory } from 'react-router-dom';
import { SINGLE_ESTIMATE_ROUTE } from '../../../constants';
import {
    getProviderSpecialties,
    getNameFromProvider,
    estimatedCostDisplayEstimate,
} from '../../../utils/EstimateDisplay.util';
import { getMemberPays } from '../../../utils/costBreakdownUtil';
import { checkIndemnityCoverages } from '../../../utils/SearchPageUtil';
import { unitMessage } from '../../../utils/EstimatePageUtil';

const Estimate = (props) => {
    const {
        estimate,
        provider,
        setProvider,
        setOtherLocationsOpen,
        setClosestLocationIndex,
        allLocations,
        setEstimate,
    } = props;

    const handleOtherLocationsOpen = () => {
        setProvider(provider);
        setEstimate(estimate);
        setOtherLocationsOpen(true);
    };

    const { setSingleEstimateResponse, memberDetails, formValues } = useContext(FormContext);
    const { selectedService } = formValues;
    const carePathServiceTypes = selectedService?.value?.fullCarePath?.serviceTypes;
    const history = useHistory();
    const componentRef = useRef();
    const onBeforeGetContentResolve = useRef();
    const isIndemnity = checkIndemnityCoverages(memberDetails, true);

    const [showAvgTagModal, setShowAvgTagModal] = useState(false);

    const handleViewSingleProvider = () => {
        setSingleEstimateResponse(estimate);
        setProvider(provider);
        history.push(SINGLE_ESTIMATE_ROUTE);
    };

    useEffect(() => {
        if (typeof onBeforeGetContentResolve.current === 'function') {
            onBeforeGetContentResolve.current();
        }
    });

    useEffect(() => {
        //Use the map service to figure out the closest/best provider locations based on criteria .
        setClosestLocationIndex([1, 0, 2, 3, 4]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (provider?.locations) {
            provider.locations.map((location) => {
                allLocations.push(location);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    //Will need map service to figure this one out. Does not have to be the closest, just the best option available and then descend.
    // const closestLocationIndex = [2, 1, 3, 4, 5];
    const allLocationIDs = [];
    allLocations.map((locationId) => {
        allLocationIDs.push(locationId);
    });

    const gridRef = useRef(null);
    const isDesktopView = useDetectMobile(gridRef);

    const costShare = estimate?.costShare;
    const costScale = estimate?.costScale;

    return (
        <Box
            sx={{ width: '100%' }}
            style={{ marginTop: '24px' }}
            data-testid={`estimate-${estimate.id}`}
            ref={componentRef}
            key={`estimate-box-${estimate.id}`}
        >
            <Grid container direction={isDesktopView ? 'row' : 'column'} ref={gridRef}>
                <Grid item xs={4}>
                    <Grid container>
                        <Grid item>
                            <Typography.H4
                                data-testid="provider-name"
                                style={{ color: '#323334', marginBottom: '0px' }}
                            >
                                {getNameFromProvider(provider)}
                            </Typography.H4>
                            <Typography.Paragraph
                                style={{ color: '#333333', marginBottom: '0px' }}
                                data-testid="providerSpecialty"
                            >
                                {getProviderSpecialties(provider)}
                            </Typography.Paragraph>
                        </Grid>
                        <Grid item xs={12} sx={{ paddingTop: '25px' }}>
                            {!isIndemnity[
                                selectedService?.value?.fullCarePath?.coverageTypeCode
                            ] && (
                                <Typography.Paragraph
                                    style={{
                                        color: estimate?.claims?.[0]?.procedures?.[0]?.provider
                                            .isInNetwork
                                            ? '#007000'
                                            : '#c40000',
                                        fontWeight: '700',
                                        marginBottom: '4px',
                                    }}
                                    data-testid="provider-tier"
                                >
                                    {estimate?.claims?.[0]?.procedures?.[0]?.provider.isInNetwork
                                        ? 'In-Network Provider'
                                        : 'Out of Network Provider'}
                                </Typography.Paragraph>
                            )}
                        </Grid>
                    </Grid>
                    <Grid container spacing={1} sx={{ paddingBottom: '25px' }}>
                        <Grid item xs={12}>
                            <Grid container alignItems="center" direction="row">
                                <CheckCircle
                                    style={{
                                        color: '#007000',
                                        height: '16px',
                                        width: '16px',
                                        marginRight: '8px',
                                    }}
                                />
                                <Typography.Paragraph
                                    style={{
                                        color: '#333333',
                                        marginBottom: '0px',
                                        fontWeight: '400',
                                        fontSize: '13px',
                                    }}
                                    data-testid="patientAcceptance"
                                >
                                    {provider?.acceptingNewPatients?.toLowerCase() === 'true'
                                        ? 'Accepting All Patients'
                                        : 'Accepting Existing Patients'}
                                </Typography.Paragraph>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            {estimate?.claims?.[0]?.procedures?.[0]?.provider.providerType.toLowerCase() ===
                                'person' && (
                                <Typography.Paragraph
                                    style={{
                                        color: '#333333',
                                        marginBottom: '0px',
                                        fontWeight: '400',
                                        fontSize: '13px',
                                    }}
                                    data-testid="gender"
                                >
                                    Gender:{' '}
                                    {provider?.gender?.toLowerCase() === 'm' ? 'Male' : 'Female'}
                                </Typography.Paragraph>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {estimate?.claims?.[0]?.procedures?.[0]?.provider.providerType.toLowerCase() ===
                                'person' && (
                                <Typography.Paragraph
                                    style={{
                                        color: '#333333',
                                        marginBottom: '0px',
                                        fontWeight: '400',
                                        fontSize: '13px',
                                    }}
                                    data-testid="availableLanguages"
                                >
                                    Language(s):{' '}
                                    {provider?.locAndPractRole?.length > 0
                                        ? provider.locAndPractRole?.[0]?.languages?.join(', ')
                                        : 'default'}
                                    {/* 
                                        TODO ADD ACTUAL LANGUAGES BC WE GOTTEM 
                                        EVENTUALLY LINE UP THE LOCATION WITH THE LANGUAGES SPOKEN BETTER SOMEHOW...
                                        come up with something better than default
                                */}
                                </Typography.Paragraph>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={4} sx={{ paddingBottom: '25px' }}>
                    <Grid container direction="column" sx={{ height: '100%' }}>
                        <Grid item>
                            <Typography.Paragraph
                                style={{
                                    color: '#333333',
                                    fontWeight: '500',
                                    fontSize: '14.22px',
                                    marginBottom: '9px',
                                }}
                                data-testid="addressLocation"
                            >
                                {provider?.locAndPractRole?.length > 0
                                    ? provider?.locAndPractRole?.[0]?.singleAddress?.line[0]
                                    : 'street1'}
                                ,{' '}
                                {provider?.locAndPractRole?.length > 0
                                    ? provider?.locAndPractRole?.[0]?.singleAddress?.city
                                    : 'city'}
                                ,{' '}
                                {provider?.locAndPractRole?.length > 0
                                    ? provider?.locAndPractRole?.[0]?.singleAddress?.state
                                    : 'state'}{' '}
                                {provider?.locAndPractRole?.length > 0
                                    ? provider?.locAndPractRole?.[0]?.singleAddress?.zipCode
                                    : 'zip'}
                            </Typography.Paragraph>
                            <Grid container alignItems="center" direction="row">
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Typography.Paragraph
                                        role="button"
                                        aria-haspopup="dialog"
                                        onClick={() => {
                                            handleOtherLocationsOpen();
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key == 'Enter' || e.key == ' ') {
                                                handleOtherLocationsOpen();
                                            }
                                        }}
                                        tabIndex="0"
                                        style={{
                                            color: '#196ECF',
                                            fontWeight: '700',
                                            fontSize: '12.64px',
                                            marginBottom: '0px',
                                            cursor: 'pointer',
                                        }}
                                        data-testid="viewOtherLocations"
                                    >
                                        View other locations & costs
                                    </Typography.Paragraph>
                                    <ChevronRight
                                        style={{
                                            color: '#196ECF',
                                            height: '16px',
                                            width: '16px',
                                            cursor: 'pointer',
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid item sx={{ marginTop: '28px' }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Grid container alignItems="center">
                                        <LocationOn
                                            style={{
                                                color: '#333333',
                                                height: '16px',
                                                width: '16px',
                                                marginRight: '15px',
                                            }}
                                        />
                                        <Typography.Paragraph
                                            style={{
                                                color: '#333333',
                                                marginBottom: '0px',
                                                fontWeight: '500',
                                                fontSize: '12.64px',
                                            }}
                                            data-testid="providerLocationDistance"
                                        >
                                            {provider?.locAndPractRole?.length > 0 &&
                                            provider?.locAndPractRole?.[0]?.distance
                                                ? parseFloat(
                                                      provider?.locAndPractRole?.[0]?.distance
                                                  )?.toFixed(2)
                                                : '0.6'}{' '}
                                            Miles Away
                                        </Typography.Paragraph>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container alignItems="center">
                                        <LocalPhone
                                            style={{
                                                color: '#333333',
                                                height: '16px',
                                                width: '16px',
                                                marginRight: '15px',
                                            }}
                                        />
                                        <Typography.Paragraph
                                            style={{
                                                color: '#333333',
                                                marginBottom: '0px',
                                                fontWeight: '500',
                                                fontSize: '12.64px',
                                            }}
                                            data-testid="phoneNumber"
                                        >
                                            {provider?.locAndPractRole?.length > 0 &&
                                            provider?.locAndPractRole?.[0]?.phoneNums?.length > 0
                                                ? provider?.locAndPractRole?.[0]?.phoneNums[0]
                                                : 'defaultPhoneNum'}
                                        </Typography.Paragraph>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid container alignItems="center">
                                        <Tty
                                            style={{
                                                color: '#333333',
                                                height: '16px',
                                                width: '16px',
                                                marginRight: '15px',
                                            }}
                                            data-testid="ttyNumber"
                                        />
                                        <Typography.Paragraph
                                            style={{
                                                color: '#333333',
                                                marginBottom: '0px',
                                                fontWeight: '500',
                                                fontSize: '12.64px',
                                            }}
                                        >
                                            TTY: 711
                                        </Typography.Paragraph>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={4}>
                    <Grid
                        container
                        justifyContent="space-between"
                        sx={{ height: '100%' }}
                        spacing={1}
                    >
                        <Grid item xs={12}>
                            <Grid container justifyContent="space-between">
                                <Grid item xs={12} sx={{ marginBottom: '14px' }}>
                                    <Grid container alignItems="center">
                                        <Grid paddingBottom="30px">
                                            <Typography.Paragraph
                                                style={{
                                                    color: '#5A5A5A',
                                                    marginBottom: '0px',
                                                    fontWeight: '500',
                                                    fontSize: '16px',
                                                }}
                                            >
                                                You pay:
                                            </Typography.Paragraph>
                                            <Grid container alignItems="center" wrap="nowrap">
                                                <Typography.Paragraph
                                                    style={{
                                                        color: '#333333',
                                                        marginBottom: '0px',
                                                        fontWeight: '700',
                                                        fontSize: '25.63px',
                                                        marginLeft: '8px',
                                                    }}
                                                    data-testid="estimatePrice"
                                                >
                                                    {getMemberPays(estimate, 'Not Available')}
                                                </Typography.Paragraph>
                                                <Typography.Paragraph
                                                    style={{
                                                        color: '#333333',
                                                        marginBottom: '0px',
                                                        fontWeight: '500',
                                                        fontSize: '14px',
                                                        marginLeft: '8px',
                                                    }}
                                                >
                                                    {unitMessage(carePathServiceTypes)}
                                                </Typography.Paragraph>
                                            </Grid>
                                        </Grid>
                                        <Grid item xs={1}></Grid>
                                        <Grid
                                            style={{
                                                paddingBottom:
                                                    costScale &&
                                                    !isIndemnity[
                                                        selectedService?.value?.fullCarePath
                                                            ?.coverageTypeCode
                                                    ]
                                                        ? '0px'
                                                        : '30px',
                                                marginTop:
                                                    costScale &&
                                                    !isIndemnity[
                                                        selectedService?.value?.fullCarePath
                                                            ?.coverageTypeCode
                                                    ]
                                                        ? '-30px'
                                                        : '0px',
                                            }}
                                        >
                                            <Typography.Paragraph
                                                style={{
                                                    color: '#5A5A5A',
                                                    marginBottom: '0px',
                                                    fontWeight: '500',
                                                    fontSize: '16px',
                                                }}
                                            >
                                                Estimated Total Cost:
                                            </Typography.Paragraph>
                                            <Grid container alignItems="center" wrap="nowrap">
                                                <Typography.Paragraph
                                                    style={{
                                                        color: '#333333',
                                                        marginBottom: '0px',
                                                        fontWeight: '700',
                                                        fontSize: '25.63px',
                                                        marginLeft: '8px',
                                                    }}
                                                    data-testid="youPayPrice"
                                                >
                                                    {estimatedCostDisplayEstimate(costShare)}
                                                </Typography.Paragraph>
                                                <Typography.Paragraph
                                                    style={{
                                                        color: '#333333',
                                                        marginBottom: '0px',
                                                        fontWeight: '500',
                                                        fontSize: '14px',
                                                        marginLeft: '8px',
                                                    }}
                                                >
                                                    {unitMessage(carePathServiceTypes)}
                                                </Typography.Paragraph>
                                            </Grid>
                                            {/* <Grid item data-testid="averagePriceThreshold">
                                                {costScale &&
                                                    !isIndemnity[
                                                        selectedService?.value?.fullCarePath
                                                            ?.coverageTypeCode
                                                    ] && (
                                                        <AverageThresholdTag
                                                            costScale={costScale}
                                                            cost={costShare?.outOfPocket}
                                                            currentDialogState={showAvgTagModal}
                                                            setDialogState={setShowAvgTagModal}
                                                        />
                                                    )}
                                            </Grid> */}
                                            {/* //commenting out the average tag for now but will put behind feature flag */}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            sx={{
                                marginTop: isDesktopView ? '30px' : '16px',
                                [`@media print`]: {
                                    display: 'none',
                                },
                            }}
                        >
                            <Button
                                buttonType="secondary-one"
                                onClick={handleViewSingleProvider}
                                data-testid="selectProviderButton"
                            >
                                Select provider & view estimate {'>'}
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12}>
                    <Divider style={{ marginTop: '24px' }} />
                </Grid>
            </Grid>
        </Box>
    );
};

export default Estimate;
